const TRANSFER_COLOR = "var(--color-primary)";
const DELEGATE_COLOR = "var(--color-blue)";

function AgentNode({
  x,
  y,
  label,
  sub,
  isOrchestrator = false,
}: {
  x: number;
  y: number;
  label: string;
  sub: string;
  isOrchestrator?: boolean;
}) {
  const w = 148;
  const h = 60;
  return (
    <g transform={`translate(${x - w / 2}, ${y - h / 2})`}>
      <rect
        width={w}
        height={h}
        rx={10}
        fill={isOrchestrator ? "oklch(0.6 0.15 165 / 0.18)" : "var(--color-bg-elevated)"}
        stroke={isOrchestrator ? "var(--color-primary)" : "var(--color-border)"}
        strokeWidth={isOrchestrator ? 1.5 : 1}
      />
      <text
        x={w / 2}
        y={22}
        textAnchor="middle"
        fill={isOrchestrator ? "var(--color-primary-text)" : "var(--color-text)"}
        fontSize={11}
        fontWeight={700}
        fontFamily="var(--font-sans)"
      >
        {label}
      </text>
      <text
        x={w / 2}
        y={39}
        textAnchor="middle"
        fill="var(--color-text-faint)"
        fontSize={9.5}
        fontFamily="var(--font-sans)"
      >
        {sub}
      </text>
    </g>
  );
}

function ArrowHead({ x, y, angle, color }: { x: number; y: number; angle: number; color: string }) {
  const size = 6;
  return (
    <polygon
      points={`0,0 ${-size},${-size / 2} ${-size},${size / 2}`}
      fill={color}
      transform={`translate(${x},${y}) rotate(${angle})`}
    />
  );
}

function EventLabel({
  x,
  y,
  label,
  color,
}: {
  x: number;
  y: number;
  label: string;
  color: string;
}) {
  return (
    <g>
      <rect
        x={x - 34}
        y={y - 10}
        width={68}
        height={19}
        rx={9}
        fill={color === TRANSFER_COLOR ? "oklch(0.6 0.15 165 / 0.15)" : "rgba(96,165,250,0.12)"}
        stroke={color}
        strokeWidth={0.8}
      />
      <text
        x={x}
        y={y + 4}
        textAnchor="middle"
        fill={color}
        fontSize={8.5}
        fontWeight={600}
        fontFamily="var(--font-mono)"
      >
        {label}
      </text>
    </g>
  );
}

export default function MultiAgentDiagram() {
  // Viewbox: 600 x 320
  const W = 600;
  const H = 320;

  // Node positions
  const orch = { x: W / 2, y: 70 };
  const research = { x: 110, y: 230 };
  const billing = { x: W / 2, y: 230 };
  const support = { x: W - 110, y: 230 };

  return (
    <section id="orchestration" className="py-20">
      <div className="mb-12">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
          Multi-agent routing
        </p>
        <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
          Orchestration you can
          <br />
          reason about.
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-text-muted">
          Transfer hands off full ownership. Delegation sends a subtask and returns. Both are
          validated at write-time, enforced at runtime, and recorded as durable routing events — so
          you always know exactly what happened and why.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-bg-secondary/60 p-6 md:p-10">
        {/* SVG diagram — hidden on very small screens, shown from sm up */}
        <div className="hidden sm:block">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            role="img"
            style={{ maxWidth: W, display: "block", margin: "0 auto" }}
            aria-label="Multi-agent routing diagram showing orchestrator transferring to sub-agents"
          >
            {/* Transfer lines — solid emerald */}
            {/* Orch → Research */}
            <line
              x1={orch.x - 40}
              y1={orch.y + 30}
              x2={research.x + 30}
              y2={research.y - 30}
              stroke={TRANSFER_COLOR}
              strokeWidth={1.5}
              strokeOpacity={0.7}
            />
            <ArrowHead x={research.x + 32} y={research.y - 28} angle={-42} color={TRANSFER_COLOR} />
            {/* Orch → Billing */}
            <line
              x1={orch.x}
              y1={orch.y + 30}
              x2={billing.x}
              y2={billing.y - 30}
              stroke={TRANSFER_COLOR}
              strokeWidth={1.5}
              strokeOpacity={0.7}
            />
            <ArrowHead x={billing.x} y={billing.y - 28} angle={90} color={TRANSFER_COLOR} />
            {/* Orch → Support */}
            <line
              x1={orch.x + 40}
              y1={orch.y + 30}
              x2={support.x - 30}
              y2={support.y - 30}
              stroke={TRANSFER_COLOR}
              strokeWidth={1.5}
              strokeOpacity={0.7}
            />
            <ArrowHead x={support.x - 32} y={support.y - 28} angle={42} color={TRANSFER_COLOR} />

            {/* Delegation arc — dashed blue — Research back to Orch */}
            <path
              d={`M ${research.x + 74} ${research.y - 10} Q ${(research.x + orch.x) / 2} ${orch.y - 30} ${orch.x - 74} ${orch.y + 10}`}
              fill="none"
              stroke={DELEGATE_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              strokeOpacity={0.65}
            />
            <ArrowHead x={orch.x - 72} y={orch.y + 12} angle={-145} color={DELEGATE_COLOR} />

            {/* Event labels */}
            <EventLabel
              x={(orch.x - 40 + research.x + 30) / 2 - 18}
              y={150}
              label="transfer"
              color={TRANSFER_COLOR}
            />
            <EventLabel
              x={(research.x + 74 + orch.x - 74) / 2 + 10}
              y={116}
              label="delegation"
              color={DELEGATE_COLOR}
            />

            {/* Nodes */}
            <AgentNode
              x={orch.x}
              y={orch.y}
              label="Orchestrator"
              sub="routes & delegates"
              isOrchestrator
            />
            <AgentNode
              x={research.x}
              y={research.y}
              label="Research Agent"
              sub="knowledge retrieval"
            />
            <AgentNode
              x={billing.x}
              y={billing.y}
              label="Billing Agent"
              sub="payment & invoicing"
            />
            <AgentNode x={support.x} y={support.y} label="Support Agent" sub="issue resolution" />
          </svg>
        </div>

        {/* Mobile fallback — simple vertical list */}
        <div className="flex flex-col items-center gap-3 sm:hidden">
          {[
            { label: "Orchestrator", sub: "routes & delegates", primary: true },
            { label: "Research Agent", sub: "knowledge retrieval", primary: false },
            { label: "Billing Agent", sub: "payment & invoicing", primary: false },
            { label: "Support Agent", sub: "issue resolution", primary: false },
          ].map((node) => (
            <div
              key={node.label}
              className={`w-full max-w-xs rounded-xl border px-4 py-3 text-center ${
                node.primary ? "border-primary bg-primary-dim" : "border-border bg-bg-elevated"
              }`}
            >
              <div
                className={`text-sm font-bold ${node.primary ? "text-primary-text" : "text-text"}`}
              >
                {node.label}
              </div>
              <div className="mt-0.5 text-xs text-text-faint">{node.sub}</div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <svg width="28" height="10" aria-hidden>
              <line x1="0" y1="5" x2="22" y2="5" stroke={TRANSFER_COLOR} strokeWidth="1.5" />
              <polygon points="22,5 16,2 16,8" fill={TRANSFER_COLOR} />
            </svg>
            <span className="text-xs text-text-faint">Transfer — full ownership handoff</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="28" height="10" aria-hidden>
              <line
                x1="0"
                y1="5"
                x2="22"
                y2="5"
                stroke={DELEGATE_COLOR}
                strokeWidth="1.5"
                strokeDasharray="4 2"
              />
              <polygon points="22,5 16,2 16,8" fill={DELEGATE_COLOR} />
            </svg>
            <span className="text-xs text-text-faint">Delegation — subtask, returns result</span>
          </div>
        </div>
      </div>

      {/* Caption */}
      <p className="mt-6 text-center text-sm font-medium text-text-muted">
        Every routing decision is recorded.&nbsp; Every handoff is validated.&nbsp; Every run is
        replayable.
      </p>
    </section>
  );
}
